import type { TaskList } from '@awork/features/task/models/task-list.model'
import type { Task } from '@awork/features/task/models/task.model'
import type { User } from '@awork/features/user/models/user.model'
import type { Project } from '@awork/features/project/models/project.model'
import type { ProjectType } from '@awork/features/project/models/project-type.model'
import type { Company } from '@awork/features/company/models/company.model'
import type { GroupByOptions } from '@awork/_shared/models/group-by-options.model'
import type { WorkType } from '@awork/features/task/models/work-type.model'
import type { SortByOption } from '@awork/_shared/models/sort-by-option.model'
import type { TaskListUI } from '@awork/features/task/models/task-list-ui.model'
import type { TaskListTimeline } from '@awork/features/task/models/task-list-timeline.model'

export enum UpcomingGroup {
  TODAY = 'TODAY',
  TOMORROW = 'TOMORROW',
  NEXT_7_DAYS = 'NEXT_7_DAYS',
  NEXT_30_DAYS = 'NEXT_30_DAYS',
  THIS_WEEK = 'THIS_WEEK',
  NEXT_WEEK = 'NEXT_WEEK',
  THIS_MONTH = 'THIS_MONTH',
  NEXT_MONTH = 'NEXT_MONTH',
  RECENTLY_CLOSED = 'RECENTLY_CLOSED',
  UPCOMING = 'UPCOMING',
  UNSCHEDULED = 'UNSCHEDULED'
}

export const UNGROUPED_TASKS_ID = 'UNGROUPED_TASKS'
export const PRIVATE_TASKS_ID = 'PRIVATE_TASKS'
export const SEARCH_RESULTS_ID = 'SEARCH_RESULTS_ID'
export const SPACE_ITEM_ID = 'SPACE_ITEM_ID'
export const ADD_TASK_ID = 'ADD_TASK_ID'

export type SubtasksByParentMap = Map<string, { parent: Task; subtasks: Task[] }>

export interface FlattenItemsData {
  flattenItems: FlattenItem[]
  subtasksByParentMap: SubtasksByParentMap
  tasksByGroupMap?: Map<string, Task[]>
}

export interface ViewOptions {
  isListCollapsed?: (listId: string) => boolean
  isTaskCollapsed?: (taskId: string) => boolean
  canEditProjectTasksFn?: (project: Project) => boolean
  groupOption?: GroupByOptions
  sortingOption?: SortByOption
  showAllDoneTasks?: boolean
  showRecentDoneTasks?: boolean
  showScheduledTasks?: boolean
  showPlannedTasks?: boolean
  searchString?: string
  taskLists?: TaskList[]
  hideOrphanSubtask?: boolean
  allowTaskCreation?: boolean
  spaceBetweenGroups?: boolean
  areListsCollapsible?: boolean
  showTasksOnlyInPrimaryList?: boolean
  generateTimelineGroups?: boolean
  filterName?: string
}

export interface GenericGroup {
  id: string
  name: string
}

export type GroupingEntity =
  User | WorkType | Project | ProjectType | Company | TaskList | GenericGroup | TaskListUI | TaskListTimeline

export type FlattenItemType = 'group' | 'task' | 'subTask' | 'new' | 'space'

export interface GroupedTask {
  task: Task
  group: GroupingEntity
}

export interface FlattenGenericItem {
  id: string
  entity: Task | GroupingEntity | null
  type: FlattenItemType
  groupId?: string
  group?: GroupingEntity
  parentTaskId?: string
  hasSubtasks?: boolean
  isFirstSubtask?: boolean
  isLastSubtask?: boolean
  isLastInGroup?: boolean
  isFirstInGroup?: boolean
  isLastTopLevelTask?: boolean
  permissions?: {
    read?: boolean
    edit?: boolean
    drag?: boolean
  }
}

export interface FlattenTask extends FlattenGenericItem {
  entity: Task
  type: 'task' | 'subTask'
  showDueToSubtask?: boolean
  clusterDueDate?: Date | null
}

export interface FlattenGroup extends FlattenGenericItem {
  entity: GroupingEntity
  type: 'group'
}

export interface FlattenEmptyItem extends FlattenGenericItem {
  entity: null
  type: 'new' | 'space'
}

export type FlattenItem = FlattenTask | FlattenGroup | FlattenEmptyItem
export type FlattenItems = (FlattenTask | FlattenGroup | FlattenEmptyItem)[]
